// init.js

import { state } from './state';
import { animate } from './animation';

/**
 * Initialise navigation interactivity
 * @param {string} selector - Base class name used for the nav
 * @param {number} offset - Scroll offset to determine nav visibility
 */
export const init = (selector, offset = 350) => {
  const navs = document.querySelectorAll(
    `.${selector}--initial, .${selector}--scrolled`,
  );
  if (!navs.length) return;

  // Setup toggle logic for each nav instance
  navs.forEach(component => {
    const toggle = component.querySelector(`.${selector}-menu button`);
    if (!toggle) return;

    let focusTrapCleanup = null;

    // Trap focus within nav when open
    const trapFocus = container => {
      const focusableSelectors = [
        'a[href]',
        'button',
        'textarea',
        'input[type="text"]',
        'input[type="radio"]',
        'input[type="checkbox"]',
        'select',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      const focusableElements = container.querySelectorAll(focusableSelectors);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTab = e => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      };

      document.addEventListener('keydown', handleTab);

      if (firstElement) firstElement.focus();

      focusTrapCleanup = () =>
        document.removeEventListener('keydown', handleTab);
    };

    // Release focus trap
    const releaseFocusTrap = () => {
      if (typeof focusTrapCleanup === 'function') {
        focusTrapCleanup();
        focusTrapCleanup = null;
      }
    };

    // Toggle menu open/close
    toggle.addEventListener('click', () => {
      state.isOpen = !state.isOpen;

      const visibleNav = [...navs].find(
        nav =>
          nav.classList.contains('is-visible') ||
          nav.classList.contains(`${selector}--initial`),
      );
      if (!visibleNav) return;

      animate(visibleNav);
      visibleNav.classList.toggle(`${selector}--open`);
      document.documentElement.classList.toggle(`${selector}--open`);

      if (state.isOpen) {
        trapFocus(visibleNav);
      } else {
        releaseFocusTrap();
      }
    });
  });

  // Handle nav visibility on scroll
  const scrolledNav = document.querySelector(`.${selector}--scrolled`);
  if (!scrolledNav) return;

  let lastScroll = window.scrollY;
  let navHeight;

  const setNavHeight = () => {
    navHeight = document.querySelector(`.${selector}`).offsetHeight;
  };
  setNavHeight();
  const handleScroll = () => {
    const currentScroll = window.scrollY;
    const scrollingUp = currentScroll < lastScroll;
    const scrollingDown = currentScroll > lastScroll;
    const isNearTop = currentScroll < navHeight + offset;

    if (scrollingUp) {
      scrolledNav.classList.add('scrolling-up');
      scrolledNav.classList.remove('scrolling-down');
    }

    if (scrollingDown) {
      scrolledNav.classList.add('scrolling-down');
      scrolledNav.classList.remove('scrolling-up');
    }

    if (isNearTop) {
      scrolledNav.classList.remove('trigger');
    }

    if (!isNearTop) {
      scrolledNav.classList.add('trigger');
    }

    if (scrollingUp && !isNearTop) {
      scrolledNav.classList.add('is-visible');
      scrolledNav.removeAttribute('aria-hidden');
      scrolledNav.removeAttribute('tabindex');
      scrolledNav.removeAttribute('inert');
    } else {
      scrolledNav.classList.remove('is-visible');
      scrolledNav.setAttribute('aria-hidden', 'true');
      scrolledNav.setAttribute('tabindex', '-1');
      scrolledNav.setAttribute('inert', '');
    }

    lastScroll = currentScroll;
  };

  window.addEventListener('scroll', handleScroll);

  // Re-animate nav on resize if open
  window.addEventListener('resize', () => {
    setNavHeight();

    if (state.isOpen && !state.hasAnimated) {
      const visibleNav = [...navs].find(
        nav =>
          nav.classList.contains('is-visible') ||
          nav.classList.contains(`${selector}--initial`),
      );
      if (visibleNav) animate(visibleNav);
    }
  });
};
